/**
 * Admin Users Page
 * 
 * Displays a list of all users in the database with their names,
 * email addresses, number of swings analyzed, last login, and training plan.
 * Allows admins to reset passwords and change training plans.
 * 
 * PLA-141: V3 is now the primary pipeline. The V3 feature flags shown here
 * are for EMERGENCY ROLLBACK only. All users use V3 by default regardless
 * of their flag settings. Per-user flags only take effect when the global
 * flag is explicitly set to 'false' in system_config for emergency rollback.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SectionHero } from "@/components/admin/primitives";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { useToast } from "@/hooks/use-toast";
import { Users, Mail, Activity, Key, Calendar, Clock, RefreshCw } from "lucide-react";
import { Navigate } from "react-router-dom";
import { getUsersWithAdminDetails, resetUserPassword, updateUserTrainingPlan, getAllTrainingPlans, updateUserV3Flags, type UserWithAdminDetails } from "@/lib/api/admin/users";
import { supabase } from "@/integrations/supabase/client";

interface GlobalV3Flags {
  analysis_v3_enabled: boolean;
  coaching_v3_enabled: boolean;
  snapshot_v2_enabled: boolean;
}

export default function UsersPage() {
  const { isPlatformAdmin, isLoading: adminLoading } = useAdminAccess();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithAdminDetails | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");

  const { data: users, isLoading, error } = useQuery<UserWithAdminDetails[]>({
    queryKey: ["admin_users_list"],
    queryFn: getUsersWithAdminDetails,
    enabled: isPlatformAdmin,
  });

  // Fetch global V3 feature flags to determine if toggles should be enabled
  const { data: globalFlags } = useQuery<GlobalV3Flags>({
    queryKey: ["global_v3_flags"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_config')
        .select('key, value')
        .in('key', ['analysis_v3_enabled', 'coaching_v3_enabled', 'snapshot_v2_enabled']);
      
      if (error) {
        console.error('Error fetching global V3 flags:', error);
        // PLA-141: Default to TRUE (V3 is primary) when flags can't be fetched
        return {
          analysis_v3_enabled: true,
          coaching_v3_enabled: true,
          snapshot_v2_enabled: true
        };
      }
      
      // PLA-141: Default to TRUE (V3 is primary) - flags exist only for emergency rollback
      const flags: GlobalV3Flags = {
        analysis_v3_enabled: true,
        coaching_v3_enabled: true,
        snapshot_v2_enabled: true
      };
      
      data?.forEach(flag => {
        const value = flag.value?.toLowerCase() === 'true';
        if (flag.key === 'analysis_v3_enabled') flags.analysis_v3_enabled = value;
        if (flag.key === 'coaching_v3_enabled') flags.coaching_v3_enabled = value;
        if (flag.key === 'snapshot_v2_enabled') flags.snapshot_v2_enabled = value;
      });
      
      return flags;
    },
    enabled: isPlatformAdmin,
  });

  // Fetch available training plans for the plan change dialog
  // Use edge function to bypass RLS and get all training plans
  const { data: trainingPlans, isLoading: trainingPlansLoading, error: trainingPlansError } = useQuery({
    queryKey: ["admin_training_plans"],
    queryFn: getAllTrainingPlans,
    enabled: changePlanDialogOpen && !!selectedUser && isPlatformAdmin,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ userId, password }: { userId: string; password: string }) =>
      resetUserPassword(userId, password),
    onSuccess: () => {
      toast({
        title: "Password Reset Successful",
        description: `Password has been reset for ${selectedUser?.email}`,
      });
      setResetPasswordDialogOpen(false);
      setNewPassword("");
      setSelectedUser(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Password Reset Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updatePlanMutation = useMutation({
    mutationFn: ({ userId, planId }: { userId: string; planId: string }) =>
      updateUserTrainingPlan(userId, planId),
    onSuccess: () => {
      toast({
        title: "Training Plan Updated",
        description: `Training plan has been updated for ${selectedUser?.email}`,
      });
      queryClient.invalidateQueries({ queryKey: ["admin_users_list"] });
      setChangePlanDialogOpen(false);
      setSelectedPlanId("");
      setSelectedUser(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateRangeModeMutation = useMutation({
    mutationFn: async ({ userId, enabled }: { userId: string; enabled: boolean }) => {
      const { error } = await supabase
        .from('profiles')
        .update({ range_mode_enabled: enabled })
        .eq('user_id', userId);
      
      if (error) throw error;
      return { ok: true };
    },
    onSuccess: (_, variables) => {
      toast({
        title: "Range Mode Updated",
        description: `Range Mode has been ${variables.enabled ? 'enabled' : 'disabled'}`,
      });
      queryClient.invalidateQueries({ queryKey: ["admin_users_list"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateV3FlagsMutation = useMutation({
    mutationFn: updateUserV3Flags,
    onSuccess: (_, variables) => {
      toast({
        title: "V3 Flags Updated",
        description: `Experimental features updated for user`,
      });
      queryClient.invalidateQueries({ queryKey: ["admin_users_list"] });
    },
    onError: (error: Error) => {
      toast({
        title: "Update Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleResetPassword = (user: UserWithAdminDetails) => {
    setSelectedUser(user);
    setNewPassword("");
    setResetPasswordDialogOpen(true);
  };

  const handleChangePlan = (user: UserWithAdminDetails) => {
    setSelectedUser(user);
    setSelectedPlanId(user.training_plan_id || "");
    setChangePlanDialogOpen(true);
  };

  const handleResetPasswordSubmit = () => {
    if (!selectedUser || !newPassword) return;
    resetPasswordMutation.mutate({ userId: selectedUser.user_id, password: newPassword });
  };

  const handleChangePlanSubmit = () => {
    if (!selectedUser || !selectedPlanId) return;
    updatePlanMutation.mutate({ userId: selectedUser.user_id, planId: selectedPlanId });
  };

  if (adminLoading) {
    return (
      <div className="min-h-screen bg-surface pb-20">
        <div className="max-w-admin mx-auto px-admin-content-padding py-8">
          <Skeleton className="h-32 w-full mb-8" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!isPlatformAdmin) {
    return <Navigate to="/admin" replace />;
  }

  const totalUsers = users?.length || 0;
  const totalSwings = users?.reduce((sum, user) => sum + user.swings_analyzed, 0) || 0;
  const activeUsers = users?.filter(user => user.swings_analyzed > 0).length || 0;

  return (
    <div className="min-h-screen bg-surface pb-20">
      <div className="max-w-admin mx-auto px-admin-content-padding py-8">
        {/* Hero Section */}
        <SectionHero
          title="User Management"
          description="View all users in the database with their swing analysis statistics"
          gradient="primary"
          accessory={
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                <Users className="h-3 w-3 mr-1" />
                {totalUsers} Total Users
              </Badge>
            </div>
          }
        />

        {/* Summary Stats */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalUsers.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeUsers.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {totalUsers > 0 ? ((activeUsers / totalUsers) * 100).toFixed(1) : 0}% of total
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Swings Analyzed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalSwings.toLocaleString()}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {activeUsers > 0 ? (totalSwings / activeUsers).toFixed(1) : 0} per active user
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Users Table */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>All Users</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : error ? (
              <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 text-center">
                <p className="text-sm text-destructive mb-2">
                  Error loading users
                </p>
                <p className="text-xs text-muted-foreground">
                  {error instanceof Error ? error.message : "Unknown error"}
                </p>
              </div>
            ) : !users || users.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">No users found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email Address</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Training Plan</TableHead>
                      <TableHead>Range Mode</TableHead>
                      <TableHead>V3 Pipeline (Emergency Rollback Only)</TableHead>
                      <TableHead className="text-right">Swings</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            {user.full_name || "Unknown"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            {user.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="h-3 w-3" />
                            {user.created_at ? new Date(user.created_at).toLocaleDateString() : "N/A"}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Clock className="h-3 w-3" />
                            {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : "Never"}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.training_plan_id ? (
                            <Badge variant="outline" className="text-xs">
                              {user.training_plan_id.substring(0, 8)}...
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">None</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={user.range_mode_enabled || false}
                              onCheckedChange={(checked) => {
                                updateRangeModeMutation.mutate({
                                  userId: user.user_id,
                                  enabled: checked,
                                });
                              }}
                              disabled={updateRangeModeMutation.isPending}
                            />
                            <span className="text-xs text-muted-foreground">
                              {user.range_mode_enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {/* PLA-141: V3 is now primary. These flags are for emergency rollback only. */}
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={user.analysis_v3_enabled ?? true}
                                onCheckedChange={(checked) => {
                                  updateV3FlagsMutation.mutate({
                                    userId: user.user_id,
                                    flags: { analysis_v3_enabled: checked },
                                  });
                                }}
                                disabled={updateV3FlagsMutation.isPending}
                                size="sm"
                              />
                              <span className="text-xs text-muted-foreground">
                                Analysis V3
                                {globalFlags?.analysis_v3_enabled && (
                                  <span className="block text-xs text-green-600">(V3 Primary)</span>
                                )}
                                {!globalFlags?.analysis_v3_enabled && (
                                  <span className="block text-xs text-destructive">(Rollback active)</span>
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={user.coaching_v3_enabled ?? true}
                                onCheckedChange={(checked) => {
                                  updateV3FlagsMutation.mutate({
                                    userId: user.user_id,
                                    flags: { coaching_v3_enabled: checked },
                                  });
                                }}
                                disabled={updateV3FlagsMutation.isPending}
                                size="sm"
                              />
                              <span className="text-xs text-muted-foreground">
                                Coaching V3
                                {globalFlags?.coaching_v3_enabled && (
                                  <span className="block text-xs text-green-600">(V3 Primary)</span>
                                )}
                                {!globalFlags?.coaching_v3_enabled && (
                                  <span className="block text-xs text-destructive">(Rollback active)</span>
                                )}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Switch
                                checked={user.snapshot_v2_enabled ?? true}
                                onCheckedChange={(checked) => {
                                  updateV3FlagsMutation.mutate({
                                    userId: user.user_id,
                                    flags: { snapshot_v2_enabled: checked },
                                  });
                                }}
                                disabled={updateV3FlagsMutation.isPending}
                                size="sm"
                              />
                              <span className="text-xs text-muted-foreground">
                                Snapshot V2
                                {globalFlags?.snapshot_v2_enabled && (
                                  <span className="block text-xs text-green-600">(V2 Primary)</span>
                                )}
                                {!globalFlags?.snapshot_v2_enabled && (
                                  <span className="block text-xs text-destructive">(Rollback active)</span>
                                )}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant={user.swings_analyzed > 0 ? "default" : "secondary"}>
                            <Activity className="h-3 w-3 mr-1" />
                            {user.swings_analyzed}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleResetPassword(user)}
                            >
                              <Key className="h-3 w-3 mr-1" />
                              Reset Password
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleChangePlan(user)}
                            >
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Change Plan
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Reset Password Dialog */}
        <Dialog 
          open={resetPasswordDialogOpen} 
          onOpenChange={(open) => {
            setResetPasswordDialogOpen(open);
            if (!open) {
              setNewPassword("");
              setSelectedUser(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                Reset password for {selectedUser?.email || "user"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                />
                <p className="text-xs text-muted-foreground">
                  Password must be at least 8 characters with at least one letter and one number.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setResetPasswordDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleResetPasswordSubmit}
                disabled={!newPassword || newPassword.length < 8 || resetPasswordMutation.isPending}
              >
                {resetPasswordMutation.isPending ? "Resetting..." : "Reset Password"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Training Plan Dialog */}
        <Dialog 
          open={changePlanDialogOpen} 
          onOpenChange={(open) => {
            setChangePlanDialogOpen(open);
            if (!open) {
              setSelectedPlanId("");
              setSelectedUser(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Change Training Plan</DialogTitle>
              <DialogDescription>
                Update training plan for {selectedUser?.email || "user"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="training-plan">Training Plan</Label>
                {trainingPlansLoading ? (
                  <div className="text-sm text-muted-foreground">Loading training plans...</div>
                ) : (
                  <>
                    <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                      <SelectTrigger id="training-plan">
                        <SelectValue placeholder="Select a training plan" />
                      </SelectTrigger>
                      <SelectContent>
                        {trainingPlans && trainingPlans.length > 0 ? (
                          trainingPlans.map((plan: any) => (
                            <SelectItem key={plan.id} value={plan.id}>
                              {plan.primary_track || 'Unknown Track'} - {plan.skill_level || 'Unknown Level'} 
                              {' '}({new Date(plan.created_at).toLocaleDateString()})
                            </SelectItem>
                          ))
                        ) : (
                          <div className="px-2 py-1.5 text-sm text-muted-foreground">
                            No training plans found
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                    {trainingPlansError && (
                      <p className="text-xs text-destructive">
                        Error loading training plans: {trainingPlansError instanceof Error ? trainingPlansError.message : 'Unknown error'}
                      </p>
                    )}
                    {!trainingPlansError && trainingPlans && trainingPlans.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No training plans available in the system. Training plans are created when users complete the training plan quiz.
                      </p>
                    )}
                    {!trainingPlansError && trainingPlans && trainingPlans.length > 0 && (
                      <p className="text-xs text-muted-foreground">
                        {trainingPlans.length} training plan{trainingPlans.length !== 1 ? 's' : ''} available
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setChangePlanDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePlanSubmit}
                disabled={!selectedPlanId || updatePlanMutation.isPending}
              >
                {updatePlanMutation.isPending ? "Updating..." : "Update Plan"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

